import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Megaphone, Flame, DollarSign, Eye, MousePointerClick } from 'lucide-react';
import { FeaturedListingsManager } from './FeaturedListingsManager';
import { AdCampaignsManager } from './AdCampaignsManager';
import { startOfMonth } from 'date-fns';

export function AdOperationsDashboard() {
  const { user, hasRole } = useAuth();
  const isLead = hasRole('ad_operations_lead');

  const { data: stats, isLoading } = useQuery({
    queryKey: ['adops-stats', user?.id, isLead],
    queryFn: async () => {
      const monthStart = startOfMonth(new Date()).toISOString();
      const today = new Date().toISOString().slice(0, 10);

      const featuredQ = supabase.from('featured_listings').select('price_paid, is_active, end_date').gte('created_at', monthStart);
      const campaignsQ = supabase.from('ad_campaigns').select('price_paid, impressions, clicks, is_active, end_date').gte('created_at', monthStart);

      const [{ data: feat }, { data: camp }] = await Promise.all([featuredQ, campaignsQ]);

      const activeFeatured = (feat || []).filter(f => f.is_active && f.end_date >= today).length;
      const activeCampaigns = (camp || []).filter(c => c.is_active && c.end_date >= today).length;
      const revenue = [...(feat || []), ...(camp || [])].reduce((s, x: any) => s + (x.price_paid || 0), 0);
      const impressions = (camp || []).reduce((s, x: any) => s + (x.impressions || 0), 0);
      const clicks = (camp || []).reduce((s, x: any) => s + (x.clicks || 0), 0);

      return { activeFeatured, activeCampaigns, revenue, impressions, clicks };
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-primary" />
          Sales Support & Ad Operations
        </h2>
        <p className="text-muted-foreground">Manage paid promotions and sidebar ad campaigns</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {isLoading ? (
          [1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)
        ) : (
          <>
            <Card><CardContent className="p-4 flex items-center gap-3"><Flame className="h-8 w-8 text-amber-500" /><div><p className="text-2xl font-bold">{stats?.activeFeatured || 0}</p><p className="text-xs text-muted-foreground">Active Featured</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><Megaphone className="h-8 w-8 text-blue-500" /><div><p className="text-2xl font-bold">{stats?.activeCampaigns || 0}</p><p className="text-xs text-muted-foreground">Active Campaigns</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><DollarSign className="h-8 w-8 text-emerald-500" /><div><p className="text-2xl font-bold">{(stats?.revenue || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Revenue (ETB)</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><Eye className="h-8 w-8 text-purple-500" /><div><p className="text-2xl font-bold">{(stats?.impressions || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Impressions</p></div></CardContent></Card>
            <Card><CardContent className="p-4 flex items-center gap-3"><MousePointerClick className="h-8 w-8 text-pink-500" /><div><p className="text-2xl font-bold">{(stats?.clicks || 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Clicks</p></div></CardContent></Card>
          </>
        )}
      </div>

      <FeaturedListingsManager />
      <AdCampaignsManager />
    </div>
  );
}
